import { useCallback, useMemo, useState } from 'react';
import { useLocalisation } from '@archipro-website/localisation';
import type { EnquirerType, EnquiryProjectKind } from 'generated/graphql';
import type {
    PublicEnquiryForm,
    PublicEnquiryFormType,
} from '../types/public-enquiry-form';
import type { PredictedDestination } from '../util/predict-enquiry-destination';
import type { EnquiryFormSelection } from './use-enquiry';
import {
    EMPTY_CONFIGURED_ANSWERS,
    NO_CONFIGURED_ERRORS,
    buildConfiguredSelection,
    findSelectedType,
    getConfiguredEnquiryRules,
    getRoutingCountry,
    hasConfiguredErrors,
    predictConfiguredDestination,
    validateConfiguredAnswers,
    type ConfiguredEnquiryAnswers,
    type ConfiguredEnquiryErrors,
    type ConfiguredEnquiryRuleOptions,
    type ConfiguredEnquiryRules,
} from '../domain/configured-enquiry';

export type UseConfiguredEnquiryOptions = ConfiguredEnquiryRuleOptions;

/** A submit's Forms half, once the answers have passed validation. */
export interface ConfiguredEnquirySubmission {
    /** A routing rule declines these answers; there is nothing to send. */
    declined: boolean;
    /** Where the client expects the answers to route. Null without a form. */
    predicted: PredictedDestination | null;
    /** The choices to hand `submitEnquiry`. */
    selection: EnquiryFormSelection;
}

export interface ConfiguredEnquiryModel {
    /** The configured form, or null on the flat-settings path. */
    form: PublicEnquiryForm | null;
    /** Which questions show and which are required. */
    rules: ConfiguredEnquiryRules;
    answers: ConfiguredEnquiryAnswers;
    /** Only populated once `validate` has run; each clears as its question is answered. */
    errors: ConfiguredEnquiryErrors;
    selectedType: PublicEnquiryFormType | undefined;
    /** ISO alpha-2 code of where the visitor appears to be. */
    visitorCountry: string | null;
    setEnquiryTypeId: (typeId: string) => void;
    setEnquirerType: (value: EnquirerType) => void;
    setProjectKind: (value: EnquiryProjectKind) => void;
    setFieldAnswer: (key: string, value: string) => void;
    /**
     * The routing prediction for a location region name and, on forms that
     * ask, the chosen branch. Null without a form.
     */
    predict: (
        regionName: string | null,
        branchId?: number | null,
    ) => PredictedDestination | null;
    /** Shows every error at once; true when the answers can be sent. */
    validate: () => boolean;
    /** Builds the submission; call after `validate` passes. */
    prepareSubmission: (
        regionName: string | null,
        branchId?: number | null,
    ) => ConfiguredEnquirySubmission;
    /** Clears answers and errors, e.g. after a successful send. */
    reset: () => void;
}

/**
 * Headless model of a Business Manager Forms enquiry: which questions a form
 * asks, the visitor's answers to them, their validation, routing prediction and
 * the submission they add to the contact details. Any enquiry UI renders it in
 * its own design; the contact details themselves stay with `useEnquiry`.
 *
 * With `form` null every rule is the flat-settings default and the model adds
 * nothing to a submission, so hosts behave exactly as they did without Forms.
 */
export function useConfiguredEnquiry(
    form: PublicEnquiryForm | null,
    { allowLocation, locationWithoutForm }: UseConfiguredEnquiryOptions = {},
): ConfiguredEnquiryModel {
    const rules = useMemo(
        () =>
            getConfiguredEnquiryRules(form, {
                allowLocation,
                locationWithoutForm,
            }),
        [form, allowLocation, locationWithoutForm],
    );
    const [answers, setAnswers] = useState(EMPTY_CONFIGURED_ANSWERS);
    const [errors, setErrors] = useState(NO_CONFIGURED_ERRORS);
    const { country } = useLocalisation();
    const visitorCountry = country ?? null;

    const setEnquiryTypeId = useCallback((typeId: string) => {
        setAnswers((current) => ({ ...current, typeId }));
        setErrors((current) => ({ ...current, enquiryType: null }));
    }, []);
    const setEnquirerType = useCallback((enquirerType: EnquirerType) => {
        setAnswers((current) => ({ ...current, enquirerType }));
        setErrors((current) => ({ ...current, enquirerType: null }));
    }, []);
    const setProjectKind = useCallback((projectKind: EnquiryProjectKind) => {
        setAnswers((current) => ({ ...current, projectKind }));
        setErrors((current) => ({ ...current, projectKind: null }));
    }, []);
    const setFieldAnswer = useCallback((key: string, value: string) => {
        setAnswers((current) => ({
            ...current,
            fieldAnswers: { ...current.fieldAnswers, [key]: value },
        }));
        setErrors((current) => {
            if (!(key in current.fields)) return current;
            const { [key]: _removed, ...fields } = current.fields;
            return { ...current, fields };
        });
    }, []);

    const routingCountry = useMemo(
        () =>
            getRoutingCountry(
                rules.customFields,
                answers.fieldAnswers,
                visitorCountry,
            ),
        [rules.customFields, answers.fieldAnswers, visitorCountry],
    );

    const predict = useCallback(
        (regionName: string | null, branchId?: number | null) =>
            predictConfiguredDestination(form, rules, answers, {
                country: routingCountry,
                regionName,
                branchId,
            }),
        [form, rules, answers, routingCountry],
    );

    const validate = useCallback(() => {
        const nextErrors = validateConfiguredAnswers(rules, answers);
        setErrors(nextErrors);
        return !hasConfiguredErrors(nextErrors);
    }, [rules, answers]);

    const prepareSubmission = useCallback(
        (
            regionName: string | null,
            branchId?: number | null,
        ): ConfiguredEnquirySubmission => {
            const predicted = predict(regionName, branchId);
            return {
                declined: predicted?.destination === 'Message',
                predicted,
                selection: buildConfiguredSelection(rules, answers, predicted),
            };
        },
        [predict, rules, answers],
    );

    const reset = useCallback(() => {
        setAnswers(EMPTY_CONFIGURED_ANSWERS);
        setErrors(NO_CONFIGURED_ERRORS);
    }, []);

    return {
        form,
        rules,
        answers,
        errors,
        selectedType: findSelectedType(rules, answers.typeId),
        visitorCountry,
        setEnquiryTypeId,
        setEnquirerType,
        setProjectKind,
        setFieldAnswer,
        predict,
        validate,
        prepareSubmission,
        reset,
    };
}
